import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore, Roles } from "stores/authStore.ts";
import { useLanguage } from "contexts/LanguageContext";
import { isDriverParentAccessEnabled } from "config/featureFlags.ts";
import {
  Building2,
  ChevronRight,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
} from "lucide-react";
import WosoolMark from "components/Brand/WosoolMark";
import LoginScene from "components/LoginScene/LoginScene";
import { loadDashboard } from "layouts/loadDashboard";
import { homePathFor } from "utils/homePath";
import { convertArabicNumerals } from "utils/phoneUtils";
import { formatRoleLabel } from "utils/roleUtils";
import { APP_STORE_LINK, PLAY_STORE_LINK } from "utils/whatsappHelpers";
import "assets/css/login.css";

// Institution cards take turns through the landing page's three accents.
const AVATAR_TONES = ["", " wl-avatar--cyan", " wl-avatar--mag"];

function Login() {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectedKindergarten, setSelectedKindergarten] = useState(null);
  const [kindergartensData, setKindergartensData] = useState([]);
  const [isRoot, setIsRoot] = useState(false);
  const [autoSelectTimeout, setAutoSelectTimeout] = useState(null);
  const [isRoleBlocked, setIsRoleBlocked] = useState(false);
  const [blockedRole, setBlockedRole] = useState(null);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(null);

  const {
    checkPhone,
    login,
    isLoading,
    error,
    isAuthenticated,
    isRoleBlocked: checkIsRoleBlocked,
    isSignedInUserBlocked: checkIsSignedInUserBlocked,
    logout,
  } =
    useAuthStore();
  const navigate = useNavigate();

  /**
   * Why an account is refused, by its role. The teacher and the sales agent
   * are refused whatever the driver/parent switch says, so the switch's
   * message would be wrong for them; a role this build does not know gets the
   * generic one.
   */
  const blockedMessage = (role) => {
    switch (role) {
      case Roles.TEACHER:
        return t("auth.teacher_blocked", "Teachers use the Wosool app. Please sign in there.");
      case Roles.SALES_AGENT:
        return t("auth.sales_agent_blocked", "Sales agents work from the Wosool app. Please sign in there.");
      case Roles.DRIVER:
      case Roles.PARENT:
        return t("auth.driver_parent_blocked", "Bus supervisor and parent access to the web dashboard is currently disabled. Please use the mobile application.");
      default:
        return t("auth.role_blocked", "Your role does not have access to the web dashboard. Please use the mobile application.");
    }
  };

  React.useEffect(() => {
    // The page's night ground under any overscroll, instead of Paper Dashboard's
    document.body.classList.add("wl-body");
    return function cleanup() {
      document.body.classList.remove("wl-body");
      // Clear any pending timeout
      if (autoSelectTimeout) {
        clearTimeout(autoSelectTimeout);
      }
    };
  }, [autoSelectTimeout]);

  React.useEffect(() => {
    if (isAuthenticated) {
      // Straight to where this person starts, not through /admin/dashboard.
      const auth = useAuthStore.getState();
      navigate(auth.user ? homePathFor(auth.user, auth) : "/admin/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // The dashboard is a chunk of its own (layouts/DashboardShell.js). Fetch it
  // once the phone number is accepted, while the password is being typed, so
  // signing in does not wait for it — and never before, so that a visit to
  // this page does not download the dashboard.
  React.useEffect(() => {
    if (currentStep < 2) return;
    const whenIdle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 200));
    whenIdle(() => {
      loadDashboard().catch(() => {
        // the sign-in itself loads it again, and reloads a stale page
      });
    });
  }, [currentStep]);

  // Handle rate limit countdown
  React.useEffect(() => {
    // Check if error is a rate limit error
    if (error && typeof error === "object" && error.status === 429) {
      const retryAfter = error.retryAfter || 60;
      setRateLimitCountdown(retryAfter);

      // Countdown timer
      const interval = setInterval(() => {
        setRateLimitCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(interval);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setRateLimitCountdown(null);
    }
  }, [error]);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;

    // Clear any existing error state and blocking state
    useAuthStore.setState({ error: null });
    setIsRoleBlocked(false);
    setBlockedRole(null);
    // A new phone starts over: a school picked for an earlier number would
    // otherwise ride along into this one's login.
    setSelectedKindergarten(null);

    try {
      const result = await checkPhone(phone);
      setIsRoot(result.isRoot);

      // Filter out blocked roles (parent, teacher) — only keep allowed kindergartens
      const allowedKindergartens = result.kindergartens.filter(kg =>
        !checkIsRoleBlocked(kg.role)
      );
      setKindergartensData(allowedKindergartens);

      // If there were kindergartens but ALL are blocked, show error
      if (!result.isRoot && result.kindergartens.length > 0 && allowedKindergartens.length === 0) {
        // The teacher's message only when every account is a teacher's: any
        // other was refused by the driver/parent switch, whose message says
        // the same thing — use the app.
        const refused =
          result.kindergartens.find((kg) => kg.role !== Roles.TEACHER) ||
          result.kindergartens[0];
        setIsRoleBlocked(true);
        setBlockedRole(refused.role);
        useAuthStore.setState({
          error: blockedMessage(refused.role),
        });
        return; // Don't proceed to next step
      }

      if (result.isRoot) {
        // Root user - show admin confirmation and proceed
        setCurrentStep(2); // Show admin confirmation step

        // Auto-proceed to password step after 2 seconds
        const timeout = setTimeout(() => {
          setCurrentStep(3);
        }, 2000);
        setAutoSelectTimeout(timeout);
      } else if (allowedKindergartens.length === 0) {
        // No allowed kindergartens found and not root
        useAuthStore.setState({
          error: t("auth.errors.noAccountsFound"),
        });
      } else if (allowedKindergartens.length === 1) {
        // Single allowed kindergarten - show selection for 2 seconds, then auto-select
        setCurrentStep(2); // Show selection step
        setSelectedKindergarten(allowedKindergartens[0]);

        // Auto-proceed to password step after 2 seconds
        const timeout = setTimeout(() => {
          setCurrentStep(3);
        }, 2000);
        setAutoSelectTimeout(timeout);
      } else {
        // Multiple allowed kindergartens - manual selection needed
        setCurrentStep(2); // Go to kindergarten selection
      }
    } catch (error) {
      // Error is already set in the store by checkPhone method
    }
  };

  const handleKindergartenSelect = (kindergarten) => {
    // Check if role is blocked before allowing selection
    if (checkIsRoleBlocked(kindergarten.role)) {
      setIsRoleBlocked(true);
      setBlockedRole(kindergarten.role);
      useAuthStore.setState({
        error: blockedMessage(kindergarten.role),
      });
      return;
    }
    
    // Clear any pending auto-selection timeout
    if (autoSelectTimeout) {
      clearTimeout(autoSelectTimeout);
      setAutoSelectTimeout(null);
    }
    setIsRoleBlocked(false);
    setBlockedRole(null);
    setSelectedKindergarten(kindergarten);
    setCurrentStep(3);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || isRoleBlocked) return;

    try {
      const kindergartenId = selectedKindergarten?.kindergarten_id;
      await login(phone, password, kindergartenId);
      
      // The real gate. `checkIsRoleBlocked` at step 2 knows only a role, from
      // an anonymous endpoint that deliberately does not say whether a
      // supervisor manages journeys — so a driver is let through to here and
      // decided now, with their user document in hand.
      const user = useAuthStore.getState().user;
      if (user && checkIsSignedInUserBlocked()) {
        // Role is blocked - logout and show error
        await logout();
        setIsRoleBlocked(true);
        setBlockedRole(user.role);
        useAuthStore.setState({
          // A sales agent and a teacher are refused whatever the driver/parent
          // switch says, so the switch's message would be wrong for them.
          error: blockedMessage(user.role),
        });
        // Reset to step 1
        setCurrentStep(1);
        setPassword("");
      }
    } catch (error) {
      // Error is already set in the store
    }
  };

  const handleBack = () => {
    // Clear any pending auto-selection timeout
    if (autoSelectTimeout) {
      clearTimeout(autoSelectTimeout);
      setAutoSelectTimeout(null);
    }

    if (currentStep === 3) {
      // If we came from kindergarten selection, go back to it
      if (
        kindergartensData.length > 1 ||
        (isRoot && kindergartensData.length === 0)
      ) {
        setCurrentStep(2);
      } else {
        setCurrentStep(1);
      }
    } else if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  // The root confirmation, without waiting out its auto-continue.
  const handleRootContinue = () => {
    if (autoSelectTimeout) {
      clearTimeout(autoSelectTimeout);
      setAutoSelectTimeout(null);
    }
    setCurrentStep(3);
  };

  // From the app-only notice back to a fresh phone step.
  const handleTryAnother = () => {
    useAuthStore.setState({ error: null });
    setIsRoleBlocked(false);
    setBlockedRole(null);
    setPhone("");
    setCurrentStep(1);
  };

  const isRootOnly = isRoot && kindergartensData.length === 0;
  const isSalesAgentBlocked = blockedRole === Roles.SALES_AGENT;
  const errorText =
    typeof error === "object" && error?.message
      ? error.message
      : typeof error === "string"
      ? t(error, error)
      : error;

  const institutionCard = (kindergarten, index, { picked = false, end = null } = {}) => (
    <>
      <span className={`wl-avatar${AVATAR_TONES[index % AVATAR_TONES.length]}`} aria-hidden="true">
        <Building2 size={21} />
      </span>
      <span className="wl-option-text">
        <span className="wl-option-name">{kindergarten.kindergarten_name}</span>
        <span className="wl-option-role">
          {picked
            ? t("auth.login.signingInAs", { role: formatRoleLabel(kindergarten.role, t) })
            : formatRoleLabel(kindergarten.role, t)}
        </span>
      </span>
      {end}
    </>
  );

  const rootCard = (end = null) => (
    <>
      <span className="wl-avatar wl-avatar--mag" aria-hidden="true">
        <ShieldCheck size={21} />
      </span>
      <span className="wl-option-text">
        <span className="wl-option-name">{t("roles.rootAdmin")}</span>
        <span className="wl-option-role">{t("auth.login.fullAccessDesc")}</span>
      </span>
      {end}
    </>
  );

  const spinner = <Loader2 className="wl-option-end animate-spin" size={20} aria-hidden="true" />;

  return (
    <div className="wl-root">
      <section className="wl-col">
        <header className="wl-head">
          <a
            className="wl-logo"
            href={`/landing${currentLanguage === "en" ? "?lang=en" : ""}`}
            aria-label={t("auth.login.brand")}
          >
            <WosoolMark />
            <span className="wl-logo-name">{t("auth.login.brand")}</span>
          </a>
          <nav className="wl-nav" aria-label={t("auth.login.brand")}>
            <a className="wl-link" href={`/landing${currentLanguage === "en" ? "?lang=en" : ""}`}>
              {t("auth.login.backToSite")}
            </a>
            <button
              type="button"
              className="wl-lang"
              lang={currentLanguage === "ar" ? "en" : "ar"}
              aria-label={t("auth.login.switchLanguage")}
              onClick={() => changeLanguage(currentLanguage === "ar" ? "en" : "ar")}
            >
              {currentLanguage === "ar" ? "EN" : "ع"}
            </button>
          </nav>
        </header>

        <main className="wl-main">
          <p className="wl-eyebrow">{t("auth.login.appTitle")}</p>
          <h1 className="wl-title">{t("auth.login.welcome")}</h1>
          <p className="wl-sub">{t("auth.login.subtitle")}</p>

          {!isRoleBlocked && (
            <div className="wl-steps" aria-hidden="true">
              {[1, 2, 3].map((step) => (
                <span key={step} className={step <= currentStep ? "is-done" : undefined} />
              ))}
            </div>
          )}

          {error && !isRoleBlocked && (
            <div className="wl-alert" role="alert">
              {errorText}
              {rateLimitCountdown !== null && rateLimitCountdown > 0 && (
                <small>{t("auth.login.retryAfter", { seconds: rateLimitCountdown })}</small>
              )}
            </div>
          )}

          {/* Parents, bus supervisors and sales agents: the app, not this page */}
          {isRoleBlocked && (
            <div className="wl-app" role="status">
              <span className="wl-app-icon" aria-hidden="true">
                <Smartphone size={22} />
              </span>
              <h2>{t("auth.login.appOnlyTitle")}</h2>
              <p>
                {isSalesAgentBlocked
                  ? t("auth.sales_agent_blocked")
                  : t("auth.driver_parent_blocked")}
              </p>
              <div className="wl-stores">
                <a className="wl-store" href={APP_STORE_LINK} target="_blank" rel="noopener noreferrer">
                  <Smartphone size={24} aria-hidden="true" />
                  <span>
                    <small>{t("auth.login.downloadOn")}</small>
                    <b>App Store</b>
                  </span>
                </a>
                <a className="wl-store" href={PLAY_STORE_LINK} target="_blank" rel="noopener noreferrer">
                  <ShoppingBag size={24} aria-hidden="true" />
                  <span>
                    <small>{t("auth.login.getItOn")}</small>
                    <b>Google Play</b>
                  </span>
                </a>
              </div>
              <button type="button" className="wl-ghost" onClick={handleTryAnother}>
                {t("auth.login.tryAnother")}
              </button>
            </div>
          )}

          {/* Step 1: phone */}
          {!isRoleBlocked && currentStep === 1 && (
            <form onSubmit={handlePhoneSubmit} className="wl-form">
              <label className="wl-field">
                <span className="wl-label">{t("auth.login.enterPhone")}</span>
                <input
                  className="wl-input wl-input--phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="username"
                  value={phone}
                  onChange={(e) => setPhone(convertArabicNumerals(e.target.value))}
                  placeholder={t("auth.login.phonePlaceholder")}
                  required
                  autoFocus
                />
                <span className="wl-hint">{t("auth.login.phoneHint")}</span>
              </label>
              <button type="submit" className="wl-btn" disabled={isLoading || !phone.trim()}>
                {isLoading ? (
                  <Loader2 className="animate-spin" size={20} aria-label={t("auth.login.continue")} />
                ) : (
                  t("auth.login.continue")
                )}
              </button>
            </form>
          )}

          {/* Step 2: which account */}
          {!isRoleBlocked && currentStep === 2 && (
            <div className="wl-stack wl-stack--tight">
              {isRootOnly ? (
                <>
                  <p className="wl-note">{t("auth.login.accountFound")}</p>
                  <div className="wl-option is-picked">{rootCard(spinner)}</div>
                  <button type="button" className="wl-btn" onClick={handleRootContinue}>
                    {t("auth.login.continueNow")}
                  </button>
                </>
              ) : kindergartensData.length === 1 ? (
                <>
                  <p className="wl-note">{t("auth.login.accountFound")}</p>
                  <div className="wl-option is-picked">
                    {institutionCard(kindergartensData[0], 0, { end: spinner })}
                  </div>
                  <button
                    type="button"
                    className="wl-btn"
                    onClick={() => handleKindergartenSelect(kindergartensData[0])}
                  >
                    {t("auth.login.continueNow")}
                  </button>
                </>
              ) : (
                <>
                  <p className="wl-note">{t("auth.login.multipleAccounts")}</p>
                  {kindergartensData.map((kindergarten, index) => (
                    <button
                      key={kindergarten.kindergarten_id}
                      type="button"
                      className="wl-option"
                      onClick={() => handleKindergartenSelect(kindergarten)}
                    >
                      {institutionCard(kindergarten, index, {
                        end: <ChevronRight className="wl-option-end wl-flip" size={20} aria-hidden="true" />,
                      })}
                    </button>
                  ))}
                </>
              )}
              <button type="button" className="wl-ghost" onClick={handleBack}>
                {t("auth.login.otherNumber")}
              </button>
            </div>
          )}

          {/* Step 3: password */}
          {!isRoleBlocked && currentStep === 3 && (
            <form onSubmit={handlePasswordSubmit} className="wl-form">
              {(isRootOnly || selectedKindergarten) && (
                <div className="wl-option wl-option--chip">
                  {isRootOnly
                    ? rootCard()
                    : institutionCard(
                        selectedKindergarten,
                        Math.max(0, kindergartensData.indexOf(selectedKindergarten)),
                        { picked: true }
                      )}
                  <button type="button" className="wl-ghost wl-ghost--accent" onClick={handleBack}>
                    {t("auth.login.change")}
                  </button>
                </div>
              )}

              {/* The phone again, for password managers to save the pair */}
              <input
                type="text"
                className="sr-only"
                autoComplete="username"
                value={phone}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
              />

              <label className="wl-field">
                <span className="wl-label">{t("auth.login.passwordPlaceholder")}</span>
                <span className="wl-pass">
                  <input
                    className="wl-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    className="wl-eye"
                    onClick={() => setShowPassword((shown) => !shown)}
                    aria-label={showPassword ? t("auth.login.hidePassword") : t("auth.login.showPassword")}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                  </button>
                </span>
              </label>

              <button
                type="submit"
                className="wl-btn"
                disabled={
                  isLoading ||
                  !password.trim() ||
                  isRoleBlocked ||
                  rateLimitCountdown !== null
                }
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={20} aria-label={t("auth.login.submit")} />
                ) : rateLimitCountdown !== null && rateLimitCountdown > 0 ? (
                  t("auth.login.retryIn", { seconds: rateLimitCountdown })
                ) : (
                  t("auth.login.submit")
                )}
              </button>
              <p className="wl-fine">{t("auth.login.forgotPassword")}</p>
            </form>
          )}
        </main>

        <footer className="wl-foot">
          <span>
            {t("auth.login.appPrompt")} <a href="/app-link">{t("auth.login.getApp")}</a>
          </span>
          <span dir="ltr">© Wosool</span>
        </footer>
      </section>

      <LoginScene />
    </div>
  );
}

export default Login;
